/**
 * СОСТОЯНИЕ ПРОВЕРКИ В КАБИНЕТЕ (WEB-2138 ч.1; сервер — WEB-2139) —
 * хранилище-спутник редактора под ключом `editorReview`.
 *
 * Сервер отдаёт `reviewState` (счётчики черновика, публикации и последней
 * фиксации «На проверку», признаки «есть изменения с последней проверки» и
 * «боевая отстаёт») в ответах открытия, сохранения, публикации и фиксации.
 * Спутник слушает эти ответы через `extraReducers` — `editorSlice.ts` (гигант)
 * не правится. Санки берутся из ФАСАДА `@/store/editorSlice`, а не из его
 * будущих частей: резка хранилища редактора спутник не ломает (шов 4 карты S2).
 *
 * Откат версии перечитывает черновик тем же открытием (`loadQuizForEditor` /
 * `loadPaywallForEditor`), поэтому отдельного слушателя отката нет — открытие
 * и есть его ответ. Снятия с публикации из редактора нет.
 *
 * Правила записи: открытие кладёт поле или стирает (другой черновик);
 * сохранение, публикация и фиксация без поля (старый сервер) известное не
 * стирают; сброс редактора стирает.
 *
 * В2 (долг кнопки, п.4а): ответ принимается только для ОТКРЫТОГО ресурса —
 * вид куска и идентификатор из аргумента санка сверяются с тем, что открыто
 * (запоминается на начале открытия). Опоздавший ответ прежнего редактора или
 * открытие, которое обогнали, строку нового ресурса не трогают. Ответ до
 * открытия (ресурс неизвестен) не принимается: сохранить можно только
 * открытое. Начало открытия ДРУГОГО ресурса стирает строку сразу, того же
 * (перечитывание) — нет, чтобы строка не мигала.
 *
 * Действие `reviewStateReceived` — дверь для ответа сервера, пришедшего НЕ
 * через санки (с теми же правилами отбора по ресурсу). Его зовёт
 * перечитывание отметки черновика (`draft/useDraftReload#refreshDraftStampOnly`):
 * ответ открытия с `reviewState` кладётся вместе с отметкой версии, опоздавший
 * ответ — нет. Сторож двух концов «ответы с reviewState ↔ слушатели спутника» —
 * `reviewStateListeners.ends.web2138.test.ts` рядом.
 *
 * WEB-2598: счётчики правок у ресурса только растут (сервер двигает их
 * прибавкой и выравниванием, назад — никогда). Ответ ТОГО ЖЕ ресурса, где
 * хоть один счётчик меньше известного, — опоздавший (гонка «На проверку» с
 * перечитыванием отметки черновика: фиксация отметку не двигает, сверка по
 * отметке такой ответ не ловит) и не принимается ни одной дверью. Равные
 * счётчики принимаются: другие признаки могли поменяться.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { components } from '@/api/generated/schema';
// Та же дверь, что у санков редактора (`editorSlice.ts`): '@/api'.
import { quizzesApi, paywallsApi } from '@/api';
import { extractApiError } from '@/api/errorStatus';
import {
  loadQuizForEditor,
  loadPaywallForEditor,
  saveQuizDraft,
  savePaywallDraft,
  publishQuiz,
  publishPaywall,
  resetEditor,
} from '@/store/editorSlice';

/**
 * Состояние проверки в хранилище: `reviewState` сервера (правда строки куска,
 * одна во всех дверях, WEB-2139) плюс признак «все воронки с куском на паузе»
 * (WEB-2595). Сервер отдаёт признак полем ответа открытия РЯДОМ с
 * `reviewState` (`QuizResponseDto.pausedInAllFunnels` /
 * `PaywallResponseDto.pausedInAllFunnels`); строке состояния удобнее держать
 * их вместе — склеивает `readReviewState`.
 */
export type ReviewState = components['schemas']['ReviewStateDto'] & {
  pausedInAllFunnels?: boolean;
};

export type ReviewResourceKind = 'quiz' | 'paywall';

export interface ReviewResource {
  kind: ReviewResourceKind;
  id: string;
}

export interface EditorReviewSliceState {
  reviewState: ReviewState | null;
  /** Открытый в редакторе ресурс; null — ничего не открыто (ответы не принимаются). */
  resource: ReviewResource | null;
}

const initialState: EditorReviewSliceState = { reviewState: null, resource: null };

function sameResource(a: ReviewResource | null, kind: ReviewResourceKind, id: string | undefined): boolean {
  return a !== null && a.kind === kind && typeof id === 'string' && a.id === id;
}

/** Проверки не было (null) — ниже любой фиксации. */
function testedOrd(v: number | null): number {
  return v === null ? -1 : v;
}

/** Хоть один счётчик ответа меньше известного — ответ опоздал (WEB-2598). */
export function goesBackwards(known: ReviewState | null, next: ReviewState): boolean {
  if (known === null) return false;
  return (
    next.draftRevision < known.draftRevision ||
    next.publishedRevision < known.publishedRevision ||
    testedOrd(next.testedRevision) < testedOrd(known.testedRevision)
  );
}

/**
 * WEB-2595: признак «все воронки с куском на паузе» сервер кладёт только в
 * ответ открытия. Ответ без признака (сохранение, публикация, фиксация, ход
 * ИИ) паузу не меняет — известный признак того же ресурса переносится.
 */
function carryPause(known: ReviewState | null, next: ReviewState): ReviewState {
  if (next.pausedInAllFunnels !== undefined || known?.pausedInAllFunnels === undefined) return next;
  return { ...next, pausedInAllFunnels: known.pausedInAllFunnels };
}

/**
 * Поле `reviewState` из тела ответа. `undefined` — поля нет или форма чужая
 * (не «сбросить», а «не знаю»): мусор в хранилище не кладётся.
 *
 * WEB-2595: признак паузы берётся только из поля тела РЯДОМ с `reviewState`
 * (`body.pausedInAllFunnels`, ответ открытия) и только булевым; внутри
 * `reviewState` его нет по контракту, и там он не ищется. Нет признака в
 * теле — нет и в результате: перенос известного делает `carryPause`.
 */
export function readReviewState(body: unknown): ReviewState | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const value = (body as { reviewState?: unknown }).reviewState;
  if (typeof value !== 'object' || value === null) return undefined;
  const v = value as Record<string, unknown>;
  if (
    typeof v.draftRevision !== 'number' ||
    typeof v.changedSinceTest !== 'boolean' ||
    typeof v.liveBehind !== 'boolean'
  ) {
    return undefined;
  }
  const { pausedInAllFunnels: _inside, ...rowTruth } = v;
  const paused = (body as { pausedInAllFunnels?: unknown }).pausedInAllFunnels;
  return (typeof paused === 'boolean' ? { ...rowTruth, pausedInAllFunnels: paused } : rowTruth) as ReviewState;
}

interface EditorSnapshot {
  editor?: { serverUpdatedAt?: string | null };
}

/** Метка черновика: переданная явно (ответ сохранения) или последняя известная. */
function baseStamp(explicit: string | undefined, getState: () => unknown): string {
  return explicit ?? (getState() as EditorSnapshot).editor?.serverUpdatedAt ?? '';
}

export const createQuizTestVersion = createAsyncThunk(
  'editorReview/createQuizTestVersion',
  async (
    { projectId, quizId, baseUpdatedAt }: { projectId: string; quizId: string; baseUpdatedAt?: string },
    { rejectWithValue, getState },
  ) => {
    try {
      const response = await quizzesApi.createTestVersion(
        projectId,
        quizId,
        baseStamp(baseUpdatedAt, getState),
      );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

export const createPaywallTestVersion = createAsyncThunk(
  'editorReview/createPaywallTestVersion',
  async (
    { projectId, paywallId, baseUpdatedAt }: { projectId: string; paywallId: string; baseUpdatedAt?: string },
    { rejectWithValue, getState },
  ) => {
    try {
      const response = await paywallsApi.createTestVersion(
        projectId,
        paywallId,
        baseStamp(baseUpdatedAt, getState),
      );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

/** Ответ сервера вне санков: тело целиком, спутник сам достаёт `reviewState`. */
export interface ReviewStateReceivedPayload extends ReviewResource {
  body: unknown;
}

const reviewSlice = createSlice({
  name: 'editorReview',
  initialState,
  reducers: {
    reviewStateReceived(state, action: PayloadAction<ReviewStateReceivedPayload>) {
      const { kind, id, body } = action.payload;
      if (!sameResource(state.resource, kind, id)) return;
      const next = readReviewState(body);
      if (next !== undefined && !goesBackwards(state.reviewState, next)) {
        state.reviewState = carryPause(state.reviewState, next);
      }
    },
  },
  extraReducers: (builder) => {
    /** Ответ сохранения, публикации или фиксации — только открытого ресурса. */
    const keepOrReplace = (
      state: EditorReviewSliceState,
      kind: ReviewResourceKind,
      id: string | undefined,
      body: unknown,
    ) => {
      if (!sameResource(state.resource, kind, id)) return;
      const next = readReviewState(body);
      if (next !== undefined && !goesBackwards(state.reviewState, next)) {
        state.reviewState = carryPause(state.reviewState, next);
      }
    };
    /** Начало открытия: другой ресурс — стереть строку сразу; тот же — ждать ответа. */
    const openStarted = (state: EditorReviewSliceState, kind: ReviewResourceKind, id: string) => {
      if (sameResource(state.resource, kind, id)) return;
      state.resource = { kind, id };
      state.reviewState = null;
    };
    /** Ответ открытия: принимается, если его не обогнало открытие другого ресурса. */
    const opened = (state: EditorReviewSliceState, kind: ReviewResourceKind, id: string, body: unknown) => {
      if (state.resource !== null && !sameResource(state.resource, kind, id)) return;
      const next = readReviewState(body) ?? null;
      // Тот же ресурс (перечитывание): опоздавший ответ со счётчиками назад
      // не принимается. Другой ресурс начало открытия уже стёрло (null).
      if (next !== null && sameResource(state.resource, kind, id) && goesBackwards(state.reviewState, next)) return;
      state.resource = { kind, id };
      state.reviewState = next;
    };
    builder
      .addCase(loadQuizForEditor.pending, (state, action) => openStarted(state, 'quiz', action.meta.arg.quizId))
      .addCase(loadPaywallForEditor.pending, (state, action) =>
        openStarted(state, 'paywall', action.meta.arg.paywallId),
      )
      .addCase(loadQuizForEditor.fulfilled, (state, action) =>
        opened(state, 'quiz', action.meta.arg.quizId, action.payload?.data),
      )
      .addCase(loadPaywallForEditor.fulfilled, (state, action) =>
        opened(state, 'paywall', action.meta.arg.paywallId, action.payload?.data),
      )
      .addCase(saveQuizDraft.fulfilled, (state, action) =>
        keepOrReplace(state, 'quiz', action.meta.arg.quizId, action.payload),
      )
      .addCase(savePaywallDraft.fulfilled, (state, action) =>
        keepOrReplace(state, 'paywall', action.meta.arg.paywallId, action.payload),
      )
      .addCase(publishQuiz.fulfilled, (state, action) =>
        keepOrReplace(state, 'quiz', action.meta.arg.quizId, action.payload),
      )
      .addCase(publishPaywall.fulfilled, (state, action) =>
        keepOrReplace(state, 'paywall', action.meta.arg.paywallId, action.payload),
      )
      .addCase(createQuizTestVersion.fulfilled, (state, action) =>
        keepOrReplace(state, 'quiz', action.meta.arg.quizId, action.payload?.quiz),
      )
      .addCase(createPaywallTestVersion.fulfilled, (state, action) =>
        keepOrReplace(state, 'paywall', action.meta.arg.paywallId, action.payload?.paywall),
      )
      .addCase(resetEditor, () => initialState);
  },
});

export const { reviewStateReceived } = reviewSlice.actions;

/** Состояние проверки; хранилище без спутника (наборы со своим стором) — null. */
export function selectReviewState(state: unknown): ReviewState | null {
  return (state as { editorReview?: EditorReviewSliceState }).editorReview?.reviewState ?? null;
}

export default reviewSlice.reducer;
